// Artist, style and museum hub pages: one indexable page per artist, art style
// or museum with enough works in the collection. Pure functions so the static
// build, the client and the tests agree on slugs and contents.
import { museumKey, museumValues } from "./museums";

export type HubKind = "artist" | "style" | "museum";

export interface HubArtwork {
  id: number;
  title: string;
  artist: string;
  date?: string;
  style?: string;
  genre?: string;
  // The museum the work is located in, when it is one with a museum key.
  museum?: string;
  imageUrl?: string;
  // The three emotions that stand out most for this work: [name, 0-100 score].
  emotions?: EmotionScore[];
}

export type EmotionScore = [string, number];

export interface HubLink {
  name: string;
  slug: string;
  count: number;
}

export interface Hub {
  kind: HubKind;
  name: string;
  slug: string;
  count: number;
  intro: string;
  yearFrom?: number;
  yearTo?: number;
  topEmotions: string[];
  // Average score on each named emotion: this hub against the whole collection.
  profile: { emotion: string; hub: number; all: number }[];
  // For an artist: the styles they worked in. For a style or a museum: its
  // leading artists.
  related: HubLink[];
  // For an artist: other artists in their main style. For a style: other styles
  // its leading artists also worked in. For a museum: other museums holding
  // works by its leading artists.
  seeAlso: HubLink[];
  artworks: HubArtwork[];
}

export interface HubIndex {
  artists: Record<string, string>; // artist name -> slug
  styles: Record<string, string>; // style name -> slug
  museums: Record<string, string>; // museumKey(located_in) -> slug
}

// What differs between the hub kinds: where they are listed and what their
// link sections are called.
export interface HubKindConfig {
  indexPath: string;
  indexName: string; // breadcrumb label
  listTitle: string; // heading of the index page
  indexField: keyof HubIndex;
  related: { heading: string; kind: HubKind };
  seeAlso: { heading: string; kind: HubKind };
  cardShowsArtist: boolean; // works by several artists, so name each work's artist
  filterPlaceholder?: string; // index pages long enough to need a filter box
}

export const HUB_KINDS: Record<HubKind, HubKindConfig> = {
  artist: {
    indexPath: "/artists/",
    indexName: "Artists",
    listTitle: "Artists A–Z",
    indexField: "artists",
    related: { heading: "Styles", kind: "style" },
    seeAlso: { heading: "Similar artists", kind: "artist" },
    cardShowsArtist: false,
    filterPlaceholder: "Filter artists",
  },
  style: {
    indexPath: "/styles/",
    indexName: "Art styles",
    listTitle: "Art styles and movements",
    indexField: "styles",
    related: { heading: "Leading artists", kind: "artist" },
    seeAlso: { heading: "Related styles", kind: "style" },
    cardShowsArtist: true,
  },
  museum: {
    indexPath: "/museums/",
    indexName: "Museums",
    listTitle: "Museums and galleries",
    indexField: "museums",
    related: { heading: "Leading artists", kind: "artist" },
    seeAlso: { heading: "Museums with works by the same artists", kind: "museum" },
    cardShowsArtist: true,
    filterPlaceholder: "Filter museums",
  },
};

// One row of the /artists/, /styles/ and /museums/ index pages: the hub's
// link and count, the years most of its works were made, and its first few
// related hubs (an artist's main style; a style's or museum's leading artists).
export interface HubIndexEntry extends HubLink {
  years?: string;
  related?: HubLink[];
}

export const HUB_INDEX_RELATED: Record<HubKind, { label: string; n: number }> = {
  artist: { label: "", n: 1 },
  style: { label: "leading artists: ", n: 3 },
  museum: { label: "leading artists: ", n: 3 },
};

export function hubIndexEntry(hub: Hub): HubIndexEntry {
  const { yearFrom, yearTo } = hub;
  const years = yearFrom && yearTo ? (yearFrom === yearTo ? `${yearFrom}` : `${yearFrom}–${yearTo}`) : undefined;
  const related = hub.related.slice(0, HUB_INDEX_RELATED[hub.kind].n);
  return { name: hub.name, slug: hub.slug, count: hub.count, ...(years ? { years } : {}), ...(related.length ? { related } : {}) };
}

// "(1,234 works, 1860–1910)" after an index entry's name.
export function hubIndexDetails(e: Pick<HubIndexEntry, "count" | "years">): string {
  return `${fmtCount(e.count)} ${e.count === 1 ? "work" : "works"}${e.years ? `, ${e.years}` : ""}`;
}

const SITE_SUFFIX = " — Ariadne's Gallery";
const TITLE_MAX = 60;

export function hubTitle(hub: Pick<Hub, "kind" | "name" | "count">): string {
  const n = fmtCount(hub.count);
  if (hub.kind === "artist") return `${hub.name}: ${n} artworks${SITE_SUFFIX}`;
  if (hub.kind === "style") return `${hub.name} art: ${n} artworks${SITE_SUFFIX}`;
  // Museum names run long: keep the site name only when it fits.
  const core = /\bcollection\b/i.test(hub.name) ? `${hub.name}: ${n} artworks` : `${hub.name}: ${n} artworks in the collection`;
  return (core + SITE_SUFFIX).length <= TITLE_MAX ? core + SITE_SUFFIX : core;
}

// The hub slug for an artwork's artist, style or museum value, if it has a hub.
export function hubSlugFor(index: HubIndex | null | undefined, kind: HubKind, value: unknown): string | undefined {
  if (!index) return undefined;
  const key = kind === "museum" ? museumKey(value) : hubKey(value);
  return key ? index[HUB_KINDS[kind].indexField]?.[key] : undefined;
}

export const HUB_ARTWORK_LIMIT = 200;

// The emotions shown to readers. Valence, Arousal and Dominance are scales,
// not feelings a reader names, so they are left out.
export const NAMED_EMOTIONS = ["Sadness", "Joy", "Tension", "Anger", "Nostalgia", "Wonder", "Interest"];

export interface EmotionStats {
  mean: Record<string, number>;
  std: Record<string, number>;
}

export function emotionStats(emotions: Map<number, Record<string, number>>): EmotionStats {
  const mean: Record<string, number> = {};
  const std: Record<string, number> = {};
  for (const k of NAMED_EMOTIONS) {
    let n = 0, sum = 0, sq = 0;
    for (const e of Array.from(emotions.values())) {
      const v = e[k];
      if (typeof v !== "number") continue;
      n++; sum += v; sq += v * v;
    }
    if (!n) continue;
    mean[k] = sum / n;
    std[k] = Math.sqrt(Math.max(sq / n - mean[k] ** 2, 0)) || 1;
  }
  return { mean, std };
}

// The n emotions a work scores furthest above the collection average (in
// standard deviations), with its real 0-100 score. Ranking by the raw score
// would put Interest near the top of almost every work.
export function standoutEmotions(e: Record<string, number> | undefined, stats: EmotionStats, n = 3): EmotionScore[] {
  if (!e) return [];
  return NAMED_EMOTIONS
    .filter((k) => typeof e[k] === "number" && stats.mean[k] !== undefined)
    .map((k) => ({ k, v: e[k], z: (e[k] - stats.mean[k]) / stats.std[k] }))
    .sort((a, b) => b.z - a.z)
    .slice(0, n)
    .map(({ k, v }) => [k, Math.round(Math.max(0, Math.min(100, v)))] as EmotionScore);
}

function emotionProfile(ids: number[], emotions: Map<number, Record<string, number>>, stats: EmotionStats) {
  const sums: Record<string, number> = {};
  let n = 0;
  for (const id of ids) {
    const e = emotions.get(id);
    if (!e) continue;
    n++;
    for (const k of NAMED_EMOTIONS) if (typeof e[k] === "number") sums[k] = (sums[k] ?? 0) + e[k];
  }
  if (n < 3) return [];
  return NAMED_EMOTIONS.filter((k) => sums[k] !== undefined && stats.mean[k] !== undefined).map((k) => ({
    emotion: k,
    hub: Math.round(sums[k] / n),
    all: Math.round(stats.mean[k]),
  }));
}

// "14799" -> "14,799"
export function fmtCount(n: number): string {
  return n.toLocaleString("en-US");
}

export function hubSlug(name: string): string {
  return name
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function hubPath(kind: HubKind, slug: string): string {
  return `/${kind}/${slug}/`;
}

export function firstYear(date?: string): number | undefined {
  if (!date) return undefined;
  const m = String(date).match(/\b(\d{3,4})\b/);
  if (!m) return undefined;
  const y = parseInt(m[1], 10);
  return y > 0 && y <= new Date().getFullYear() ? y : undefined;
}

const IGNORED_NAMES = new Set(["", "unknown", "unknown artist", "anonymous", "none", "n/a"]);

// The form artist and style values are grouped and looked up by.
export function hubKey(value: unknown): string {
  return typeof value === "string" ? value.trim().replace(/\s+/g, " ").replace(/,$/, "") : "";
}
const clean = hubKey;

// WikiArt style labels carry a bracketed qualifier, e.g. "Art nouveau (modern)".
export function styleDisplayName(style: string): string {
  const short = style.replace(/\s*\([^)]*\)/g, "").trim();
  return short ? short.charAt(0).toUpperCase() + short.slice(1) : style;
}

function topCounts(values: string[], n: number): [string, number][] {
  const counts = new Map<string, number>();
  for (const v of values) if (v) counts.set(v, (counts.get(v) ?? 0) + 1);
  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, n);
}

function listPhrase(items: string[]): string {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

function yearRange(from?: number, to?: number): string {
  if (!from || !to) return "";
  return from === to ? `in ${from}` : `between ${from} and ${to}`;
}

// The 5th-95th percentile of the years, so one misdated work doesn't stretch the range.
function typicalYears(years: number[]): { yearFrom?: number; yearTo?: number } {
  if (!years.length) return {};
  const sorted = [...years].sort((a, b) => a - b);
  const pct = (p: number) => sorted[Math.min(sorted.length - 1, Math.floor(p * sorted.length))];
  return { yearFrom: pct(0.05), yearTo: pct(0.95) };
}

// "the Musée d'Orsay collection", "the Phillips Collection".
function collectionPhrase(name: string): string {
  const base = /^the\s/i.test(name) ? name : `the ${name}`;
  return /\bcollection\b/i.test(name) ? base : `${base} collection`;
}

// The emotions a group of works scores highest on relative to the whole
// collection, e.g. ["Wonder", "Nostalgia"].
function distinctiveEmotions(
  ids: number[],
  emotions: Map<number, Record<string, number>>,
  globalMean: Record<string, number>,
): string[] {
  const sums: Record<string, number> = {};
  let n = 0;
  for (const id of ids) {
    const e = emotions.get(id);
    if (!e) continue;
    n++;
    for (const [k, v] of Object.entries(e)) sums[k] = (sums[k] ?? 0) + v;
  }
  if (n < 3) return [];
  // Valence, Arousal and Dominance are dimensions, not feelings a reader names.
  const named = ["Sadness", "Joy", "Tension", "Anger", "Nostalgia", "Wonder", "Interest"];
  return named
    .filter((k) => sums[k] !== undefined && globalMean[k])
    .map((k) => [k, sums[k] / n - globalMean[k]] as [string, number])
    .filter(([, diff]) => diff > 2)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 2)
    .map(([k]) => k);
}

function sortArtworks(list: HubArtwork[]): HubArtwork[] {
  return [...list].sort((a, b) => {
    const ya = firstYear(a.date) ?? 99999;
    const yb = firstYear(b.date) ?? 99999;
    return ya - yb || a.title.localeCompare(b.title) || a.id - b.id;
  });
}

function slim(a: any): HubArtwork {
  const out: HubArtwork = { id: a.id, title: clean(a.title) || "Untitled", artist: clean(a.artist) };
  // Some dates carry a place after a semicolon: "1894 ; Oslo , Norway".
  const date = clean(String(a.date ?? "").split(";")[0]);
  if (date) out.date = date;
  if (clean(a.style)) out.style = clean(a.style);
  if (clean(a.genre)) out.genre = clean(a.genre);
  const museum = museumValues(a.located_in).find((m) => museumKey(m));
  if (museum) out.museum = museum;
  if (a.imageUrl) out.imageUrl = a.imageUrl;
  return out;
}

export function buildHubs(
  artworks: any[],
  emotions: Map<number, Record<string, number>>,
  minArtistWorks: number,
  minStyleWorks: number,
  minMuseumWorks = 25,
): { artists: Hub[]; styles: Hub[]; museums: Hub[]; index: HubIndex; stats: EmotionStats; artistWorks: Map<string, ArtistWork[]> } {
  const byArtist = new Map<string, HubArtwork[]>();
  const byStyle = new Map<string, HubArtwork[]>();
  const byMuseum = new Map<string, HubArtwork[]>(); // keyed by museumKey
  const museumSpellings = new Map<string, Map<string, number>>();
  const stats = emotionStats(emotions);
  for (const raw of artworks) {
    if (!raw || typeof raw.id !== "number") continue;
    const a = slim(raw);
    const standout = standoutEmotions(emotions.get(a.id), stats);
    if (standout.length) a.emotions = standout;
    if (a.artist && !IGNORED_NAMES.has(a.artist.toLowerCase())) {
      if (!byArtist.has(a.artist)) byArtist.set(a.artist, []);
      byArtist.get(a.artist)!.push(a);
    }
    if (a.style && !IGNORED_NAMES.has(a.style.toLowerCase())) {
      if (!byStyle.has(a.style)) byStyle.set(a.style, []);
      byStyle.get(a.style)!.push(a);
    }
    if (a.museum) {
      const key = museumKey(a.museum);
      if (!byMuseum.has(key)) byMuseum.set(key, []);
      byMuseum.get(key)!.push(a);
      const spellings = museumSpellings.get(key) ?? new Map<string, number>();
      spellings.set(a.museum, (spellings.get(a.museum) ?? 0) + 1);
      museumSpellings.set(key, spellings);
    }
  }
  // Names that differ only by accents or case are one museum, shown in its
  // most common spelling.
  const museumNames = new Map<string, string>();
  for (const [key, spellings] of Array.from(museumSpellings)) {
    const name = Array.from(spellings).sort((x, y) => y[1] - x[1] || x[0].localeCompare(y[0]))[0][0];
    museumNames.set(key, name);
    for (const a of byMuseum.get(key)!) a.museum = name;
  }
  const museumName = (key: string) => museumNames.get(key) ?? key;

  const globalSums: Record<string, number> = {};
  for (const e of Array.from(emotions.values())) for (const [k, v] of Object.entries(e) as [string, number][]) globalSums[k] = (globalSums[k] ?? 0) + v;
  const globalMean: Record<string, number> = {};
  for (const [k, v] of Object.entries(globalSums)) globalMean[k] = v / Math.max(emotions.size, 1);

  // Keep groups above the threshold whose slug is unique; on a slug clash the
  // larger group wins so every hub URL maps to exactly one name.
  function select(groups: Map<string, HubArtwork[]>, min: number, display: (n: string) => string): Map<string, string> {
    const slugs = new Map<string, string>();
    const sorted = Array.from(groups.entries())
      .filter(([, list]) => list.length >= min)
      .sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]));
    const taken = new Set<string>();
    for (const [name] of sorted) {
      const slug = hubSlug(display(name));
      if (!slug || taken.has(slug)) continue;
      taken.add(slug);
      slugs.set(name, slug);
    }
    return slugs;
  }
  const artistSlugs = select(byArtist, minArtistWorks, (n) => n);
  const styleSlugs = select(byStyle, minStyleWorks, styleDisplayName);
  const museumSlugs = select(byMuseum, minMuseumWorks, museumName);

  const link = (slugs: Map<string, string>, groups: Map<string, HubArtwork[]>, display: (n: string) => string) =>
    ([name]: [string, number]): HubLink | null =>
      slugs.has(name) ? { name: display(name), slug: slugs.get(name)!, count: groups.get(name)!.length } : null;
  const artistLink = link(artistSlugs, byArtist, (n) => n);
  const styleLink = link(styleSlugs, byStyle, styleDisplayName);
  const museumLink = link(museumSlugs, byMuseum, museumName);

  const artists: Hub[] = [];
  for (const [name, slug] of Array.from(artistSlugs)) {
    const list = sortArtworks(byArtist.get(name)!);
    const years = list.map((a) => firstYear(a.date)).filter((y): y is number => !!y);
    const yearFrom = years.length ? Math.min(...years) : undefined;
    const yearTo = years.length ? Math.max(...years) : undefined;
    const styles = topCounts(list.map((a) => a.style ?? ""), 6);
    const genres = topCounts(list.map((a) => (a.genre ?? "").toLowerCase()), 3).map(([g]) => g);
    const topEmotions = distinctiveEmotions(list.map((a) => a.id), emotions, globalMean);

    const parts = [`Ariadne's Gallery holds ${fmtCount(list.length)} works by ${name}${years.length ? `, made ${yearRange(yearFrom, yearTo)}` : ""}.`];
    if (styles.length) parts.push(`Most belong to ${listPhrase(styles.slice(0, 3).map(([s]) => styleDisplayName(s)))}.`);
    if (genres.length) parts.push(`The most common genres are ${listPhrase(genres)}.`);
    if (topEmotions.length) parts.push(`Compared with the rest of the collection, these works most often evoke ${listPhrase(topEmotions.map((e) => e.toLowerCase()))}.`);

    const mainStyle = styles[0]?.[0];
    const peers = mainStyle
      ? topCounts(byStyle.get(mainStyle)!.map((a) => a.artist).filter((a) => a !== name), 12)
      : [];

    artists.push({
      kind: "artist",
      name,
      slug,
      count: list.length,
      intro: parts.join(" "),
      yearFrom,
      yearTo,
      topEmotions,
      profile: emotionProfile(list.map((a) => a.id), emotions, stats),
      related: styles.map(styleLink).filter((l): l is HubLink => !!l),
      seeAlso: peers.map(artistLink).filter((l): l is HubLink => !!l).slice(0, 8),
      artworks: list.slice(0, HUB_ARTWORK_LIMIT),
    });
  }

  const styles: Hub[] = [];
  for (const [key, slug] of Array.from(styleSlugs)) {
    const list = sortArtworks(byStyle.get(key)!);
    const name = styleDisplayName(key);
    const years = list.map((a) => firstYear(a.date)).filter((y): y is number => !!y);
    const { yearFrom, yearTo } = typicalYears(years);
    const artistCounts = topCounts(list.map((a) => a.artist), 24);
    const artistTotal = new Set(list.map((a) => a.artist)).size;
    const genres = topCounts(list.map((a) => (a.genre ?? "").toLowerCase()), 3).map(([g]) => g);
    const topEmotions = distinctiveEmotions(list.map((a) => a.id), emotions, globalMean);

    const parts = [`Ariadne's Gallery holds ${fmtCount(list.length)} ${name} works by ${fmtCount(artistTotal)} ${artistTotal === 1 ? "artist" : "artists"}${years.length ? `, most made ${yearRange(yearFrom, yearTo)}` : ""}.`];
    if (artistCounts.length) parts.push(`The best represented ${artistCounts.length === 1 ? "artist is" : "artists are"} ${listPhrase(artistCounts.slice(0, 3).map(([a]) => a))}.`);
    if (genres.length) parts.push(`The most common genres are ${listPhrase(genres)}.`);
    if (topEmotions.length) parts.push(`Compared with the rest of the collection, ${name} works most often evoke ${listPhrase(topEmotions.map((e) => e.toLowerCase()))}.`);

    // Other styles the leading artists of this style also worked in.
    const leading = new Set(artistCounts.slice(0, 10).map(([a]) => a));
    const otherStyles = topCounts(
      artworks
        .filter((a) => a && leading.has(clean(a.artist)) && clean(a.style) && clean(a.style) !== key)
        .map((a) => clean(a.style)),
      12,
    );

    styles.push({
      kind: "style",
      name,
      slug,
      count: list.length,
      intro: parts.join(" "),
      yearFrom,
      yearTo,
      topEmotions,
      profile: emotionProfile(list.map((a) => a.id), emotions, stats),
      related: artistCounts.map(artistLink).filter((l): l is HubLink => !!l),
      seeAlso: otherStyles.map(styleLink).filter((l): l is HubLink => !!l).slice(0, 8),
      artworks: list.slice(0, HUB_ARTWORK_LIMIT),
    });
  }

  // Per museum, how many of its works each artist has, to find museums that
  // share a museum's leading artists.
  const museumArtists = new Map<string, Map<string, number>>();
  for (const key of Array.from(museumSlugs.keys())) {
    const counts = new Map<string, number>();
    for (const a of byMuseum.get(key)!) if (a.artist) counts.set(a.artist, (counts.get(a.artist) ?? 0) + 1);
    museumArtists.set(key, counts);
  }

  const museums: Hub[] = [];
  for (const [key, slug] of Array.from(museumSlugs)) {
    const list = sortArtworks(byMuseum.get(key)!);
    const name = museumName(key);
    const years = list.map((a) => firstYear(a.date)).filter((y): y is number => !!y);
    const { yearFrom, yearTo } = typicalYears(years);
    const named = list.map((a) => a.artist).filter((a) => a && !IGNORED_NAMES.has(a.toLowerCase()));
    const artistCounts = topCounts(named, 24);
    const artistTotal = new Set(named).size;
    const styles = topCounts(list.map((a) => a.style ?? "").filter((s) => !IGNORED_NAMES.has(s.toLowerCase())), 3);
    const topEmotions = distinctiveEmotions(list.map((a) => a.id), emotions, globalMean);

    const by = artistTotal ? ` by ${fmtCount(artistTotal)} ${artistTotal === 1 ? "artist" : "artists"}` : "";
    const parts = [`Ariadne's Gallery holds ${fmtCount(list.length)} works in ${collectionPhrase(name)}${by}${years.length ? `, most made ${yearRange(yearFrom, yearTo)}` : ""}.`];
    if (artistCounts.length) parts.push(`The best represented ${artistCounts.length === 1 ? "artist is" : "artists are"} ${listPhrase(artistCounts.slice(0, 3).map(([a]) => a))}.`);
    if (styles.length) parts.push(`The main ${styles.length === 1 ? "style is" : "styles are"} ${listPhrase(styles.map(([s]) => styleDisplayName(s)))}.`);
    if (topEmotions.length) parts.push(`Compared with the rest of Ariadne's Gallery, these works most often evoke ${listPhrase(topEmotions.map((e) => e.toLowerCase()))}.`);

    // Other museums with works by this museum's leading artists, ranked by how
    // many such works they hold.
    const leading = artistCounts.slice(0, 10).map(([a]) => a);
    const shared: [string, number][] = [];
    for (const [other, counts] of Array.from(museumArtists)) {
      if (other === key) continue;
      const n = leading.reduce((sum, a) => sum + (counts.get(a) ?? 0), 0);
      if (n) shared.push([other, n]);
    }
    shared.sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));

    museums.push({
      kind: "museum",
      name,
      slug,
      count: list.length,
      intro: parts.join(" "),
      yearFrom,
      yearTo,
      topEmotions,
      profile: emotionProfile(list.map((a) => a.id), emotions, stats),
      related: artistCounts.map(artistLink).filter((l): l is HubLink => !!l),
      seeAlso: shared.map(museumLink).filter((l): l is HubLink => !!l).slice(0, 8),
      artworks: list.slice(0, HUB_ARTWORK_LIMIT),
    });
  }

  const index: HubIndex = {
    artists: Object.fromEntries(artistSlugs),
    styles: Object.fromEntries(styleSlugs),
    museums: Object.fromEntries(museumSlugs),
  };
  // Every artist's works in date order, for the "More by" links on artwork pages.
  const artistWorks = new Map<string, ArtistWork[]>();
  for (const [name, list] of Array.from(byArtist)) {
    artistWorks.set(name, sortArtworks(list).map((a) => ({ id: a.id, title: a.title, ...(a.date ? { date: a.date } : {}) })));
  }
  return { artists, styles, museums, index, stats, artistWorks };
}

export interface ArtistWork {
  id: number;
  title: string;
  date?: string;
}

// The "More by {artist}" block of an artwork page: other works by the same
// artist, and the artist's hub when there is one.
export interface MoreByArtist {
  artist: string;
  slug?: string;
  count: number;
  works: ArtistWork[];
}

export const MORE_BY_ARTIST_LIMIT = 8;

// The works made just before and after this one. Taking neighbours rather than
// the artist's first few works spreads links over all of an artist's pages.
export function moreByArtist(
  artistWorks: Map<string, ArtistWork[]>,
  index: HubIndex,
  artist: unknown,
  id: number,
  n = MORE_BY_ARTIST_LIMIT,
): MoreByArtist | undefined {
  const name = clean(artist);
  const list = name ? artistWorks.get(name) : undefined;
  if (!list || list.length < 2) return undefined;
  const at = list.findIndex((w) => w.id === id);
  const others = list.filter((w) => w.id !== id);
  const centre = at < 0 ? 0 : at;
  const start = Math.max(0, Math.min(centre - Math.floor(n / 2), others.length - n));
  const slug = hubSlugFor(index, "artist", name);
  return { artist: name, ...(slug ? { slug } : {}), count: list.length, works: others.slice(start, start + n) };
}
